const CONTROL_CLASS =
    "cursor-pointer rounded-[2px] border px-[7px] py-[2px] text-[9px] uppercase tracking-[0.12em]";
const ACTION_CLASS =
    `${CONTROL_CLASS} border-[color:var(--line)] text-[color:var(--muted)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]`;
const ACTIVE_CLASS =
    `${CONTROL_CLASS} border-[color:var(--green)] bg-[color:var(--green)] text-[color:var(--panel)]`;

function statusLabel(status) {
    if (status === "schema-unavailable") return "Schema unavailable";
    if (status === "unsupported") return "Unsupported Canvas runtime";
    return "Canvas runtime conflict";
}
export function CanvasRuntimeNotice({ status = "conflict", issues = [], message = null }) {
    const detail = message || issues[0]?.message || "This Canvas interaction cannot be applied safely.";
    return (
        <div
            role="status"
            className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[8px] py-[6px] text-[10.5px] leading-[1.35] text-[color:var(--muted)]"
        >
            <span className="mr-[6px] font-mono text-[9px] uppercase tracking-[0.08em] text-[color:var(--ink)]">
                {statusLabel(status)}
            </span>
            {detail}
        </div>
    );
}

export function CanvasDisclosures({ disclosures = [] }) {
    if (!disclosures.length) return null;
    return (
        <div className="grid gap-[4px]">
            {disclosures.map((disclosure) => (
                <div
                    key={disclosure.id}
                    className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[8px] py-[5px] text-[10px] leading-[1.35] text-[color:var(--muted)]"
                >
                    <span className="mr-[6px] font-mono text-[9px] uppercase tracking-[0.08em] text-[color:var(--muted-soft)]">
                        {disclosure.label || disclosure.kind}
                    </span>
                    {disclosure.text}
                </div>
            ))}
        </div>
    );
}

function ControlGroup({ control, value, onChange }) {
    return (
        <div className="flex min-w-0 items-center gap-[4px]" role="group" aria-label={control.label}>
            <span className="mr-[2px] text-[9px] uppercase tracking-[0.1em] text-[color:var(--muted-soft)]">
                {control.label}
            </span>
            {control.options.map((option) => {
                const selected = typeof option.value === typeof value && Object.is(option.value, value);
                return (
                    <button
                        key={`${typeof option.value}:${String(option.value)}`}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onChange(control.id, option.value)}
                        className={selected ? ACTIVE_CLASS : ACTION_CLASS}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

export default function CanvasRuntimeControls({
    compiled,
    state,
    focus,
    pendingCount = 0,
    resetVisible = false,
    onControlChange,
    onClearFocus,
    onRefresh,
    onStop,
    onReset,
}) {
    if (!compiled?.enabled) return null;
    if (compiled.status !== "ready") {
        return <CanvasRuntimeNotice status={compiled.status} issues={compiled.issues} />;
    }
    return (
        <div
            data-export-ignore="true"
            className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[9px] py-[7px]"
        >
            <span className="text-[9px] uppercase tracking-[0.12em] text-[color:var(--muted-soft)]">
                Canvas controls
            </span>
            {compiled.controls.map((control) => (
                <ControlGroup
                    key={control.id}
                    control={control}
                    value={state.controls[control.id]}
                    onChange={onControlChange}
                />
            ))}
            {focus ? (
                <button
                    type="button"
                    onClick={onClearFocus}
                    className={ACTION_CLASS}
                    title="Clear the cross-view focus"
                >
                    {focus.field}: {String(focus.value)} (clear)
                </button>
            ) : null}
            <span className="min-w-[4px] flex-1" />
            <button type="button" onClick={onRefresh} className={ACTION_CLASS}>
                refresh all
            </button>
            {pendingCount ? (
                <button type="button" onClick={onStop} className={ACTION_CLASS}>
                    stop {pendingCount}
                </button>
            ) : null}
            {resetVisible ? (
                <button type="button" onClick={onReset} className={ACTION_CLASS}>
                    reset
                </button>
            ) : null}
        </div>
    );
}
